"use client";

import { Button, Checkbox, Flex, Form, FormInstance, Select } from "antd/lib";
import { useTranslation } from "react-i18next";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Event, EventParticipantsResponse, ExistingUserParticipant, NewUserParticipant } from "@/types/events";
import styles from "@/src/components/MyReporting/MyReporting.module.scss";
import { EventsEquippingLevels } from "@/constants/events";
import TextArea from "antd/lib/input/TextArea";
import { useEventsFetchers } from "@/hooks/useEventsFetchers";
import { App } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import SearchableSelect from "@/src/components/Common/SearchableSelect";
import { analytics, SelectOptionsDataType } from "@/constants/common";
import { ReportingStepFormValues } from "@/src/components/MyReporting/steps/ReportingSteps";
import { LastNameNoName } from "@/constants/users";

export interface EventParticipantsTableProps {
  eventId: string;
  participants: EventParticipantsResponse | ExistingUserParticipant[] | NewUserParticipant[] | string[];
  setParticipants?: Dispatch<SetStateAction<EventParticipantsResponse | undefined>>;
  setDeleteParticipantIsLoading?: Dispatch<SetStateAction<boolean>>;
  withActions?: boolean;
  withResults?: boolean;
  withPossibleResults?: boolean;
  isEditReportMode?: boolean;
  equippingLevel?: EventsEquippingLevels | null;
  isInReportingContext: boolean;
  withPossibilityToAdd?: boolean;
  participantsEmailsForAdd?: string[];
  setParticipantsEmailsForAdd?: Dispatch<SetStateAction<string[]>>;
  formValues?: ReportingStepFormValues;
  form?: FormInstance;
  isEquippingReportEventWithConfidentialParticipants?: Event["has_confidential_participants"];
  localOnly?: boolean;
  withMobileTestResult?: boolean;
  mobileTestResultsByEmail?: Record<string, boolean | null>;
}

type UserId = ExistingUserParticipant["id"] | NewUserParticipant["id"];

const HiddenFormField = (_props: { value?: unknown; onChange?: unknown }) => null;

const EventParticipantsTable: React.FC<EventParticipantsTableProps> = ({
  eventId,
  withActions = false,
  isInReportingContext,
  participants,
  setDeleteParticipantIsLoading,
  setParticipants,
  withResults = false,
  withPossibleResults = false,
  isEditReportMode = false,
  equippingLevel,
  withPossibilityToAdd = false,
  participantsEmailsForAdd = [],
  setParticipantsEmailsForAdd,
  formValues,
  form,
  isEquippingReportEventWithConfidentialParticipants = 0,
  localOnly = false,
  withMobileTestResult = false,
  mobileTestResultsByEmail,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { deleteEventParticipant, sendInviteToEvent } = useEventsFetchers();

  const [deleteIsLoading, setDeleteIsLoading] = useState(false);
  const [participantsIdsForDelete, setParticipantsIdsForDelete] = useState<UserId[]>([]);
  const [sendInviteIsLoading, setSendInviteIsLoading] = useState(false);
  const [participantIdForSendInvite, setParticipantIdForSendInvite] = useState<UserId>();

  useEffect(() => {
    if (setDeleteParticipantIsLoading) setDeleteParticipantIsLoading(deleteIsLoading);
  }, [deleteIsLoading]);

  const participantsArray = useMemo(() => {
    if (Array.isArray(participants)) {
      return participants.map((participant) =>
        typeof participant === "object"
          ? participant
          : ({
              id: participant,
              userId: participant,
              email: participant,
              first_name: null,
              last_name: null,
              can_resend: false,
              overqualified: false,
              mentors: [],
            } as NewUserParticipant),
      );
    } else {
      const arr: (ExistingUserParticipant | NewUserParticipant)[] = [];

      Object.entries(participants).forEach(([key, value]) => {
        if ("passed_mobile_test" in value) {
          arr.push({
            id: key,
            userId: value.id.toString(),
            email: value.email,
            first_name: value.first_name,
            last_name: value.last_name,
            can_resend: false,
            overqualified: false,
            mentors: [],
          });
          return;
        }
        arr.push({
          ...value,
          id: key,
          userId: value.id.toString(),
        });
      });

      return arr;
    }
  }, [participants]);

  const removeParticipantRowHandler = (participantsIdsForDelete: UserId[]) => {
    if (!Array.isArray(participants) && setParticipants) {
      const eventParticipants: EventParticipantsResponse = { ...participants };

      participantsIdsForDelete.forEach((id) => {
        delete eventParticipants[id];
      });

      setParticipants(eventParticipants);
    }
  };

  const removeParticipantHandler = async (
    participantId: UserId,
    participantsIdsForDelete: UserId[],
    participantWereAddedByFullName: boolean,
  ) => {
    if (localOnly) {
      removeParticipantRowHandler(participantsIdsForDelete);
      setParticipantsIdsForDelete((prevIds) => prevIds.filter((id) => id !== participantId));
      return;
    }

    if (!isEquippingReportEventWithConfidentialParticipants || participantWereAddedByFullName) {
      setDeleteIsLoading(true);

      await deleteEventParticipant(eventId, participantId.toString(), setDeleteIsLoading);
      analytics.track("Participant removed from event", { value: JSON.stringify({ eventId, participantId }) });
      removeParticipantRowHandler(participantsIdsForDelete);

      setDeleteIsLoading(false);
      setParticipantsIdsForDelete((prevIds) => prevIds.filter((id) => id !== participantId));
    } else {
      removeParticipantRowHandler(participantsIdsForDelete);
    }
  };

  const sendInviteHandler = async (participantId: UserId) => {
    setParticipantIdForSendInvite(participantId);
    setSendInviteIsLoading(true);

    await sendInviteToEvent(eventId, participantId.toString(), setSendInviteIsLoading);

    setSendInviteIsLoading(false);
    analytics.track("Participant invite resent", { value: JSON.stringify({ eventId, participantId }) });
    message.success(t("eventDetailsPage.participants.messages.resendSent"));
  };

  const participantsForAddHandler = (event: CheckboxChangeEvent) => {
    if (!participantsEmailsForAdd || !setParticipantsEmailsForAdd) throw new Error("No setEmails prop provided");

    setParticipantsEmailsForAdd(
      event.target.checked
        ? [...participantsEmailsForAdd, event.target.value]
        : participantsEmailsForAdd.filter((email) => email !== event.target.value),
    );
  };

  const allParticipantsForAddHandler = (event: CheckboxChangeEvent) => {
    if (!participantsEmailsForAdd || !setParticipantsEmailsForAdd) throw new Error("No setEmails prop provided");

    setParticipantsEmailsForAdd(
      event.target.checked
        ? [...new Set([...participantsEmailsForAdd, ...participantsArray.map((participant) => participant.email)])]
        : participantsEmailsForAdd.filter(
            (email) => !participantsArray.some((participant) => email === participant.email),
          ),
    );
  };

  return (
    <div className={styles.equippingEventParticipantsTableWrapper}>
      <table className={styles.equippingEventParticipantsTable}>
        <thead className={styles.equippingEventParticipantsTable__header}>
          <tr className={styles.equippingEventParticipantsTable__row}>
            <td className={styles.equippingEventParticipantsTable__column}>
              {t("createReportPage.sections.participants.equipping.table.firstName")}
            </td>
            <td className={styles.equippingEventParticipantsTable__column}>
              {t("createReportPage.sections.participants.equipping.table.lastName")}
            </td>
            <td className={styles.equippingEventParticipantsTable__column}>
              {t("createReportPage.sections.participants.equipping.table.primaryEmail")}
            </td>
            {withResults && (
              <>
                <td className={styles.equippingEventParticipantsTable__column}>
                  {t("createReportPage.sections.participants.equipping.table.results")}
                </td>
                {equippingLevel === EventsEquippingLevels.advanced && (
                  <td className={styles.equippingEventParticipantsTable__column}>
                    {t("createReportPage.sections.participants.equipping.results.certification")}
                  </td>
                )}
                {equippingLevel !== EventsEquippingLevels.supplemental && (
                  <td className={styles.equippingEventParticipantsTable__column}>
                    {t("createReportPage.sections.participants.equipping.results.mentor")}
                  </td>
                )}
                <td className={styles.equippingEventParticipantsTable__column}>
                  {t("createReportPage.sections.participants.equipping.results.comments")}
                </td>
              </>
            )}
            {withActions && (
              <td className={styles.equippingEventParticipantsTable__column}>
                {t("createReportPage.sections.participants.equipping.table.actions")}
              </td>
            )}
            {withMobileTestResult && (
              <td
                className={`${styles.equippingEventParticipantsTable__column} ${styles.equippingEventParticipantsTable__column_nowrap}`}
              >
                {t("mobileAudioEquippingReportPage.participants.columns.testResult")}
              </td>
            )}
            {withPossibilityToAdd && (
              <td
                className={`${styles.equippingEventParticipantsTable__column} ${styles.equippingEventParticipantsTable__column_small}`}
              >
                <Checkbox
                  checked={participantsArray.every((p) => participantsEmailsForAdd.includes(p.email))}
                  onChange={allParticipantsForAddHandler}
                />
              </td>
            )}
          </tr>
        </thead>
        <tbody>
          {participantsArray.map((participant, index) => (
            <tr key={participant.id} className={styles.equippingEventParticipantsTable_row}>
              <td className={styles.equippingEventParticipantsTable__column}>{participant.first_name || ""}</td>
              <td className={styles.equippingEventParticipantsTable__column}>
                {participant.last_name && participant.last_name !== LastNameNoName ? participant.last_name : ""}
              </td>
              <td
                className={`${styles.equippingEventParticipantsTable__column} ${styles.equippingEventParticipantsTable__column_email}`}
              >
                {participant.email}
              </td>
              {withResults && (
                <>
                  <td
                    className={`${styles.equippingEventParticipantsTable__column} ${styles.equippingEventParticipantsTable__column_withField}`}
                  >
                    {isEquippingReportEventWithConfidentialParticipants ? (
                      participant.email ? (
                        <Form.Item name={`participants[${index}][email]`} initialValue={participant.email} hidden>
                          <HiddenFormField />
                        </Form.Item>
                      ) : (
                        <>
                          <Form.Item
                            name={`participants[${index}][first_name]`}
                            initialValue={participant.first_name}
                            hidden
                          >
                            <HiddenFormField />
                          </Form.Item>
                          <Form.Item
                            name={`participants[${index}][last_name]`}
                            initialValue={participant.last_name}
                            hidden
                          >
                            <HiddenFormField />
                          </Form.Item>
                        </>
                      )
                    ) : !isEditReportMode ? (
                      <Form.Item name={`participants[${index}][id]`} initialValue={participant.id} hidden>
                        <HiddenFormField />
                      </Form.Item>
                    ) : null}
                    <Form.Item
                      name={`participants[${index}][passed]`}
                      rules={[
                        {
                          required: true,
                          message: t("createReportPage.sections.otherInfo.equipping.form.messages.passedRequired"),
                        },
                      ]}
                      initialValue={formValues?.[`participants[${index}][passed]`]}
                    >
                      <Select
                        placeholder={t("createReportPage.sections.otherInfo.equipping.form.placeholders.passed")}
                        options={[
                          { value: 1, label: t("createReportPage.sections.otherInfo.equipping.form.options.passed") },
                          {
                            value: 0,
                            label: t("createReportPage.sections.otherInfo.equipping.form.options.notPassed"),
                          },
                        ]}
                      />
                    </Form.Item>
                    {equippingLevel !== EventsEquippingLevels.advanced && (
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                          prevValues[`participants[${index}][passed]`] !==
                          currentValues[`participants[${index}][passed]`]
                        }
                      >
                        {({ getFieldValue }) => {
                          const isPassed = getFieldValue(`participants[${index}][passed]`);

                          return isPassed ? (
                            <Form.Item name={`participants[${index}][level]`} initialValue="1" hidden>
                              <HiddenFormField />
                            </Form.Item>
                          ) : null;
                        }}
                      </Form.Item>
                    )}
                  </td>
                  {equippingLevel === EventsEquippingLevels.advanced && (
                    <td
                      className={`${styles.equippingEventParticipantsTable__column} ${styles.equippingEventParticipantsTable__column_withField}`}
                    >
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                          prevValues[`participants[${index}][passed]`] !==
                          currentValues[`participants[${index}][passed]`]
                        }
                      >
                        {({ getFieldValue }) => {
                          const isPassed = getFieldValue(`participants[${index}][passed]`);

                          return isPassed ? (
                            <Form.Item
                              name={`participants[${index}][level]`}
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    "createReportPage.sections.otherInfo.equipping.form.messages.certificationRequired",
                                  ),
                                },
                              ]}
                              initialValue={formValues?.[`participants[${index}][level]`]}
                            >
                              <Select
                                placeholder={t(
                                  "createReportPage.sections.otherInfo.equipping.form.placeholders.certification",
                                )}
                                options={[
                                  {
                                    value: "2",
                                    label: t(
                                      "createReportPage.sections.otherInfo.equipping.form.options.healingGroupFacilitator",
                                    ),
                                  },
                                  {
                                    value: "3",
                                    label: t(
                                      "createReportPage.sections.otherInfo.equipping.form.options.trainingFacilitator",
                                    ),
                                  },
                                ]}
                                style={{ minWidth: 250 }}
                              />
                            </Form.Item>
                          ) : (
                            t("createReportPage.sections.participants.equipping.table.notChanged")
                          );
                        }}
                      </Form.Item>
                    </td>
                  )}
                  {equippingLevel !== EventsEquippingLevels.supplemental && (
                    <td
                      className={`${styles.equippingEventParticipantsTable__column} ${styles.equippingEventParticipantsTable__column_withField}`}
                    >
                      <Form.Item
                        name={`participants[${index}][mentor]`}
                        initialValue={formValues?.[`participants[${index}][mentor]`]}
                      >
                        <SearchableSelect
                          dataType={SelectOptionsDataType.contacts}
                          userId={participant.userId}
                          placeholder={t("createReportPage.sections.otherInfo.equipping.form.placeholders.mentor")}
                          onChange={(value, option) => {
                            if (form) form.setFieldValue(`participants[${index}][mentor_name]`, option?.label);
                          }}
                          initialItems={
                            formValues?.[`participants[${index}][mentor]`]
                              ? [
                                  {
                                    value: formValues?.[`participants[${index}][mentor]`],
                                    name: formValues?.[`participants[${index}][mentor_name]`],
                                  },
                                ]
                              : participant.mentors
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        name={`participants[${index}][mentor_name]`}
                        hidden
                        initialValue={formValues?.[`participants[${index}][mentor_name]`]}
                      >
                        <HiddenFormField />
                      </Form.Item>
                    </td>
                  )}
                  <td
                    className={`${styles.equippingEventParticipantsTable__column} ${styles.equippingEventParticipantsTable__column_withField}`}
                  >
                    <Form.Item
                      name={`participants[${index}][comments]`}
                      initialValue={formValues?.[`participants[${index}][comments]`] ?? ""}
                    >
                      <TextArea
                        rows={2}
                        placeholder={t("createReportPage.sections.otherInfo.equipping.form.placeholders.comments")}
                      />
                    </Form.Item>
                  </td>
                </>
              )}
              {withPossibleResults && (
                <td hidden>
                  {isEditReportMode || isEquippingReportEventWithConfidentialParticipants ? (
                    participant.email ? (
                      <Form.Item name={`participants[${index}][email]`} initialValue={participant.email}>
                        <HiddenFormField />
                      </Form.Item>
                    ) : (
                      <>
                        <Form.Item
                          name={`participants[${index}][first_name]`}
                          initialValue={participant.first_name}
                        >
                          <HiddenFormField />
                        </Form.Item>
                        <Form.Item name={`participants[${index}][last_name]`} initialValue={participant.last_name}>
                          <HiddenFormField />
                        </Form.Item>
                      </>
                    )
                  ) : (
                    <Form.Item name={`participants[${index}][id]`} initialValue={participant.id}>
                      <HiddenFormField />
                    </Form.Item>
                  )}
                  <Form.Item name={`participants[${index}][passed]`}>
                    <HiddenFormField />
                  </Form.Item>
                  <Form.Item name={`participants[${index}][level]`}>
                    <HiddenFormField />
                  </Form.Item>
                  {equippingLevel !== EventsEquippingLevels.supplemental && (
                    <>
                      <Form.Item name={`participants[${index}][mentor]`}>
                        <HiddenFormField />
                      </Form.Item>
                      <Form.Item name={`participants[${index}][mentor_name]`}>
                        <HiddenFormField />
                      </Form.Item>
                    </>
                  )}
                  <Form.Item name={`participants[${index}][comments]`}>
                    <HiddenFormField />
                  </Form.Item>
                </td>
              )}
              {withActions && (
                <td className={styles.equippingEventParticipantsTable__column}>
                  <Flex gap={16}>
                    <Button
                      type="text"
                      className={styles.deleteButton}
                      onClick={() => {
                        const participantsIds = [...participantsIdsForDelete, participant.id];
                        setParticipantsIdsForDelete(participantsIds);
                        return removeParticipantHandler(participant.id, participantsIds, !!participant.first_name);
                      }}
                      loading={participantsIdsForDelete.includes(participant.id) && deleteIsLoading}
                    >
                      <i className={`${styles.deleteButton__icon} icon-trash_stroke btn-icon`} />
                    </Button>
                    {!isInReportingContext && participant.can_resend && (
                      <Button
                        onClick={() => sendInviteHandler(participant.id)}
                        loading={participantIdForSendInvite === participant.id && sendInviteIsLoading}
                      >
                        <span className="btn-text">{t("eventDetailsPage.participants.buttons.resendInvite")}</span>
                      </Button>
                    )}
                  </Flex>
                </td>
              )}
              {withMobileTestResult && (
                <td className={styles.equippingEventParticipantsTable__column}>
                  {mobileTestResultsByEmail?.[participant.email] === true
                    ? t("mobileAudioEquippingReportPage.participants.testResult.passed")
                    : mobileTestResultsByEmail?.[participant.email] === false
                    ? t("mobileAudioEquippingReportPage.participants.testResult.failed")
                    : isEditReportMode
                    ? t("mobileAudioEquippingReportPage.participants.testResult.failed")
                    : t("mobileAudioEquippingReportPage.participants.testResult.notTaken")}
                </td>
              )}
              {withPossibilityToAdd && (
                <td
                  className={`${styles.equippingEventParticipantsTable__column} ${styles.equippingEventParticipantsTable__column_small}`}
                >
                  <Checkbox
                    value={participant.email}
                    checked={participantsEmailsForAdd.some((email) => email === participant.email)}
                    onChange={participantsForAddHandler}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EventParticipantsTable;
